import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import NewsletterPopup from "@/components/NewsletterPopup";
import AgeGate from "@/components/AgeGate";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import { ChatWidget } from "@/components/ChatWidget";
import { trackPageView } from "@/lib/analytics";

const Shop = lazy(() => import("@/pages/Shop"));
const WineDetail = lazy(() => import("@/pages/WineDetail"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Account = lazy(() => import("@/pages/Account"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminInventory = lazy(() => import("@/pages/AdminInventory"));
const AdminAziende = lazy(() => import("@/pages/AdminAziende"));
const Aziende = lazy(() => import("@/pages/Aziende"));
const AziendeLogin = lazy(() => import("@/pages/AziendeLogin"));
const AziendeDashboard = lazy(() => import("@/pages/AziendeDashboard"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Cart = lazy(() => import("@/pages/Cart"));
const Promo = lazy(() => import("@/pages/Promo"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const GiftCard = lazy(() => import("@/pages/GiftCard"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const ShippingReturns = lazy(() => import("@/pages/ShippingReturns"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ChiSiamo = lazy(() => import("@/pages/ChiSiamo"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

function AppRoutes() {
  const location = useLocation();
  // Callback OAuth: intercetta session_id nel fragment PRIMA di ogni altra route
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/promo" element={<Promo />} />
      <Route path="/wine/:id" element={<WineDetail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/admin/gestione-rapida" element={<AdminInventory />} />
      <Route path="/admin/aziende" element={<AdminAziende />} />
      <Route path="/aziende" element={<Aziende />} />
      <Route path="/aziende/login" element={<AziendeLogin />} />
      <Route path="/aziende/dashboard" element={<AziendeDashboard />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/termini" element={<TermsConditions />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/gift-card" element={<GiftCard />} />
      <Route path="/spedizioni" element={<ShippingReturns />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/enoteca-rovigo" element={<ChiSiamo />} />
      <Route path="/chi-siamo" element={<Navigate to="/enoteca-rovigo" replace />} />
      <Route path="/preferiti" element={<Wishlist />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const BASE_TITLE = "UvaShop | Enoteca Online e Vendita Vini con Consegna a Domicilio";
const BASE_DESC = "UvaShop, l'enoteca online dell'Enoteca Uvaspina di Rovigo: vendita vini italiani, bollicine e distillati con consegna a domicilio, spedizione in tutta Italia e ritiro in negozio.";
const PAGE_TITLES = [
  ["/shop", "Catalogo Vini | Vendita Vini Online — UvaShop"],
  ["/promo", "Promozioni Vini | UvaShop"],
  ["/cart", "Carrello | UvaShop"],
  ["/checkout", "Checkout | UvaShop"],
  ["/login", "Accedi | UvaShop"],
  ["/register", "Registrati | UvaShop"],
  ["/account", "Il mio account | UvaShop"],
  ["/admin/analytics", "Analytics | UvaShop"],
  ["/admin/gestione-rapida", "Gestione Rapida | UvaShop"],
  ["/admin/aziende", "Aziende | UvaShop"],
  ["/admin", "Dashboard | UvaShop"],
  ["/payment/success", "Ordine confermato | UvaShop"],
  ["/privacy", "Privacy Policy | UvaShop"],
  ["/termini", "Termini e Condizioni | UvaShop"],
  ["/blog", "Blog | Enoteca UvaShop"],
  ["/faq", "Domande Frequenti (FAQ) | UvaShop"],
  ["/gift-card", "Gift Card Digitali | UvaShop"],
];
const PAGE_DESCRIPTIONS = [
  ["/shop", "Catalogo completo dei vini in vendita online da UvaShop: rossi, bianchi, bollicine e distillati. Consegna a domicilio a Rovigo, spedizione in Italia e ritiro in negozio."],
  ["/promo", "Vini in promozione e offerte speciali dell'Enoteca Uvaspina. Vendita vini online con consegna a domicilio e spedizione in tutta Italia."],
  ["/gift-card", "Gift card digitali UvaShop: regala i vini dell'Enoteca Uvaspina. Consegna via email, utilizzabili in più ordini."],
  ["/blog", "Blog dell'Enoteca Uvaspina: consigli sui vini, abbinamenti e degustazioni. Approfondimenti dallo shop online UvaShop di Rovigo."],
  ["/faq", "FAQ UvaShop: spedizioni in tutta Italia, ritiro gratuito in negozio a Rovigo, consegna a domicilio, pagamenti e resi. Le risposte dell'Enoteca Uvaspina."],
];

function setMeta(name, attr, value) {
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el && value) el.setAttribute("content", value);
}

function RouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const canonicalUrl = `https://uvashop.it${pathname}`;
    const lc = document.querySelector('link[rel="canonical"]');
    if (lc) lc.setAttribute("href", canonicalUrl);
    setMeta("og:url", "property", canonicalUrl);

    // Le pagine dinamiche (/wine/:id, /blog/:slug) impostano title/description autonomamente
    if (pathname.startsWith("/wine/") || pathname.match(/^\/blog\/.+/)) return;
    const tMatch = PAGE_TITLES.find(([p]) => pathname === p || pathname.startsWith(p + "/"));
    const title = tMatch ? tMatch[1] : BASE_TITLE;
    document.title = title;
    setMeta("og:title", "property", title);
    const dMatch = PAGE_DESCRIPTIONS.find(([p]) => pathname === p || pathname.startsWith(p + "/"));
    const desc = dMatch ? dMatch[1] : BASE_DESC;
    setMeta("description", "name", desc);
    setMeta("og:description", "property", desc);
  }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageView(pathname + search);
  }, [pathname, search]);
  useEffect(() => {
    // Rinvia il page_view (con gli UTM di atterraggio) appena l'utente
    // accetta i cookie: il primo page_view era stato bloccato dal consenso.
    const onDecided = () => trackPageView(window.location.pathname + window.location.search);
    window.addEventListener("cookie:decided", onDecided);
    return () => window.removeEventListener("cookie:decided", onDecided);
  }, []);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function App() {
  return (
    <div className="App grain min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <ContentProvider>
          <CartProvider>
           <WishlistProvider>
            <SmoothScroll />
            <ScrollManager />
            <RouteTitle />
            <AnalyticsTracker />
            <Header />
            <CartDrawer />
            <main className="relative z-[2]">
              <Suspense fallback={<div className="min-h-screen" />}>
                <AppRoutes />
              </Suspense>
            </main>
            <Footer />
            <NewsletterPopup />
            <CookieBanner />
            <AgeGate />
            <ChatWidget />
            <Toaster position="bottom-right" offset={96} mobileOffset={96} />
           </WishlistProvider>
          </CartProvider>
          </ContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
